
import React from 'react';
import { ASSESSMENT_SECTIONS } from '@/data/assessmentSections';
import { useAssessmentState } from '@/hooks/useAssessmentState';
import { AssessmentNavigation } from '@/components/assessment/AssessmentNavigation';
import { AssessmentStart } from "@/components/assessment/AssessmentStart";
import ProductCraftSection from "@/components/assessment/ProductCraftSection";
import CustomerDiscoverySection from "@/components/assessment/CustomerDiscoverySection";
import ResearchDesignSection from "@/components/assessment/ResearchDesignSection";
import MarketImpactSection from "@/components/assessment/MarketImpactSection";
import ProductOperationsSection from "@/components/assessment/ProductOperationsSection";
import IndustryImpactSection from "@/components/assessment/IndustryImpactSection";
import { ScoreRevealAnimation } from '@/components/assessment/ScoreRevealAnimation';
import { ResultsDashboard } from '@/components/assessment/ResultsDashboard';
import { calculateTotalScore } from '@/utils/assessmentScoring';
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AssessmentStep } from '@/types/assessment-types';
import { useState } from 'react';

const CAREER_STAGES = [
  { name: 'Explorer', range: [0, 35], color: 'text-accent-blue', description: 'Building foundational skills' },
  { name: 'Practitioner', range: [36, 55], color: 'text-accent-green', description: 'Developing expertise' },
  { name: 'Specialist', range: [56, 75], color: 'text-accent-orange', description: 'Mastering the craft' },
  { name: 'Expert', range: [76, 95], color: 'text-accent-pink', description: 'Leading and innovating' },
  { name: 'Guru', range: [96, 120], color: 'text-primary', description: 'Shaping the industry' }
];

const ProductManagementAssessment = () => {
  const {
    currentStep,
    setCurrentStep,
    assessmentData,
    handleStart,
    handleSectionComplete,
    handleSaveResults
  } = useAssessmentState();

  const [showScoreReveal, setShowScoreReveal] = useState(false);
  const [showResultsDashboard, setShowResultsDashboard] = useState(false);

  const handleNavigateSection = (sectionId: string) => {
    if (isValidStep(sectionId)) {
      setCurrentStep(sectionId as AssessmentStep);
    }
  };

  const isValidStep = (step: string): step is AssessmentStep => {
    const validSteps: AssessmentStep[] = [
      'start',
      'productCraft',
      'customerDiscovery',
      'researchDesign',
      'marketImpact',
      'productOperations',
      'industryImpact',
      'results'
    ];
    return validSteps.includes(step as AssessmentStep);
  };

  const getCareerStage = (score: number) => {
    return CAREER_STAGES.find(stage => score >= stage.range[0] && score <= stage.range[1]) || CAREER_STAGES[0];
  };

  const handleResultsReady = () => {
    const totalScore = calculateTotalScore(assessmentData);
    
    // Check if assessment is complete
    const allSectionsComplete = ['productCraft', 'customerDiscovery', 'researchDesign', 'marketImpact', 'productOperations', 'industryImpact']
      .every(section => assessmentData[section as keyof typeof assessmentData]);

    if (allSectionsComplete) {
      setCurrentStep('results');
      setShowScoreReveal(true);
    }
  };

  const handleScoreRevealComplete = () => {
    setShowScoreReveal(false);
    setShowResultsDashboard(true);
  };

  // Check if we should show score reveal
  React.useEffect(() => {
    if (currentStep === 'results' && !showScoreReveal && !showResultsDashboard) {
      const totalScore = calculateTotalScore(assessmentData);
      if (totalScore > 0) {
        setShowScoreReveal(true);
      }
    }
  }, [currentStep, assessmentData]);

  if (showScoreReveal) {
    const totalScore = calculateTotalScore(assessmentData);
    return (
      <ScoreRevealAnimation
        totalScore={totalScore}
        careerStage={getCareerStage(totalScore)}
        onComplete={handleScoreRevealComplete}
      />
    );
  }

  if (showResultsDashboard) {
    const totalScore = calculateTotalScore(assessmentData);
    const dimensionScores = [
      {
        name: 'Product Craft & Technical Excellence',
        score: assessmentData.productCraft?.score || 0,
        maxScore: 30,
        color: 'text-accent-blue'
      },
      {
        name: 'Customer Discovery & Understanding',
        score: assessmentData.customerDiscovery?.score || 0,
        maxScore: 20,
        color: 'text-accent-green'
      },
      {
        name: 'Research & Design Mastery',
        score: assessmentData.researchDesign?.score || 0,
        maxScore: 20,
        color: 'text-accent-orange'
      },
      {
        name: 'Market & Business Impact',
        score: assessmentData.marketImpact?.score || 0,
        maxScore: 20,
        color: 'text-accent-pink'
      },
      {
        name: 'Product Operations & Team Leadership',
        score: assessmentData.productOperations?.score || 0,
        maxScore: 15,
        color: 'text-primary'
      },
      {
        name: 'Industry Impact & Influence',
        score: assessmentData.industryImpact?.score || 0,
        maxScore: 15,
        color: 'text-secondary'
      }
    ];

    return (
      <ResultsDashboard
        totalScore={totalScore}
        dimensionScores={dimensionScores}
        careerStage={getCareerStage(totalScore)}
        answers={{}} // Can be enhanced to track specific answers
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-purple-950/10 to-background py-16">
      <div className="container max-w-7xl mx-auto px-4">
        <div className="space-y-8">
          {currentStep !== 'results' && (
            <div className="bg-gradient-card rounded-xl p-6 shadow-lg">
              <h1 className="text-3xl font-bold text-center mb-6 bg-gradient-primary bg-clip-text text-transparent">
                Product Leadership Score™
              </h1>
              <p className="text-center text-text-secondary mb-6">
                Self-Assessment for Product Professionals
              </p>
              <AssessmentNavigation
                currentStep={currentStep}
                sections={ASSESSMENT_SECTIONS}
                onNavigateSection={handleNavigateSection}
              />
            </div>
          )}

          <div className="space-y-6">
            {currentStep === 'start' && (
              <AssessmentStart onStart={handleStart} />
            )}

            {currentStep === 'productCraft' && (
              <div className="animate-fade-in">
                <ProductCraftSection
                  onComplete={(data) => {
                    handleSectionComplete('productCraft', data as { score: number; responses: Record<string, number> });
                    handleResultsReady();
                  }}
                  initialData={assessmentData.productCraft}
                />
              </div>
            )}

            {currentStep === 'customerDiscovery' && (
              <div className="animate-fade-in">
                <CustomerDiscoverySection
                  onComplete={(data) => {
                    handleSectionComplete('customerDiscovery', data as { score: number; responses: Record<string, number> });
                    handleResultsReady();
                  }}
                  initialData={assessmentData.customerDiscovery}
                />
              </div>
            )}

            {currentStep === 'researchDesign' && (
              <div className="animate-fade-in">
                <ResearchDesignSection
                  onComplete={(data) => {
                    handleSectionComplete('researchDesign', data as { score: number; responses: Record<string, number> });
                    handleResultsReady();
                  }}
                  initialData={assessmentData.researchDesign}
                />
              </div>
            )}

            {currentStep === 'marketImpact' && (
              <div className="animate-fade-in">
                <MarketImpactSection
                  onComplete={(data) => {
                    handleSectionComplete('marketImpact', data as { score: number; responses: Record<string, number> });
                    handleResultsReady();
                  }}
                  initialData={assessmentData.marketImpact}
                />
              </div>
            )}

            {currentStep === 'productOperations' && (
              <div className="animate-fade-in">
                <ProductOperationsSection
                  onComplete={(data) => {
                    handleSectionComplete('productOperations', data as { score: number; responses: Record<string, number> });
                    handleResultsReady();
                  }}
                  initialData={assessmentData.productOperations}
                />
              </div>
            )}

            {currentStep === 'industryImpact' && (
              <div className="animate-fade-in">
                <IndustryImpactSection
                  onComplete={(data) => {
                    handleSectionComplete('industryImpact', data as { score: number; responses: Record<string, number> });
                    handleResultsReady();
                  }}
                  initialData={assessmentData.industryImpact}
                />
              </div>
            )}

            {currentStep === 'results' && !showScoreReveal && !showResultsDashboard && (
              <Card className="mt-8 p-8 bg-gradient-card border-0 shadow-lg text-center">
                <h3 className="text-xl font-semibold mb-4 text-text-primary">Save Your Results</h3>
                <p className="text-text-secondary mb-6">
                  Sign in to save your results, get personalized guidance, and track your progress over time.
                </p>
                <Button 
                  onClick={handleSaveResults} 
                  size="lg"
                  className="bg-primary hover:bg-primary-hover text-white px-8 py-3 rounded-lg 
                           transition-all duration-200 shadow-md hover:shadow-lg 
                           hover:scale-[1.02] border-0"
                >
                  Save Results & Get Guidance
                </Button>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductManagementAssessment;
